import { useMemo } from 'react';
import type { EntityList } from '@einhaus/project314-types';
import { useEntityLists } from '@/hooks/useEntityLists';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';

export interface EntityListSelectProps extends SearchSelectFormProps {
    value: number | null;
    onChange: (entityListId: number | null) => void;
    /** Narrows the selectable list, e.g. to exclude already-added lists */
    filter?: (entityList: EntityList) => boolean;
}

const toItem = (entityList: EntityList): SearchSelectItem => {
    const name = entityList.name || `Entity List ${entityList.id}`;
    const count = entityList.entities.length;

    return {
        label: `${name} (#${entityList.id})`,
        meta: `${count} ${count === 1 ? 'entity' : 'entities'}`,
        name,
        value: entityList.id
    };
};

const missingItem = (entityListId: number): SearchSelectItem => ({
    label: `Entity List ${entityListId} (#${entityListId})`,
    meta: 'Not in your current entity lists',
    name: `Entity list #${entityListId}`,
    value: entityListId
});

/**
 * Searchable single select for the user's saved entity lists. Fetches its own
 * data via useEntityLists, so call sites only manage the selected id. Search
 * matches name and id (with or without #).
 */
const EntityListSelect = (props: EntityListSelectProps) => {
    const { value, onChange, filter, placeholder, ...formProps } = props;

    const { entityLists, error: entityListsError, isLoading } = useEntityLists();

    const items = useMemo(() => {
        const visible = filter ? entityLists.filter(filter) : entityLists;

        return visible.map(toItem);
    }, [entityLists, filter]);

    const loadError = entityListsError ? `Failed to load entity lists: ${entityListsError.message}` : undefined;

    return (
        <SearchSelect
            {...formProps}
            isLoading={isLoading}
            items={items}
            loadError={loadError}
            mode="single"
            placeholder={placeholder ?? 'Select an entity list...'}
            resolveMissingItem={missingItem}
            value={value}
            onChange={onChange}
        />
    );
};

export default EntityListSelect;
